import type { Checklist } from '@cognite/apm-client';
import { Body } from '@cognite/cogs-core';
import type {
  DatagridColumn,
  GridColDef,
  GridRowSelectionModel,
} from '@cognite/cogs-lab';
import { DataGrid } from '@cognite/cogs-lab';
import { DateCell } from '@infield/features/activities/activity-planning/common-table-cell-components';
import type { TFunction } from 'i18next';

import {
  checklistToSchedulesDataGridRow,
  sortByMultipleStringProperties,
} from '../utils/format-data-grid-data-utils';

import * as S from './elements';
import SchedulesToolbar from './schedules-toolbar';

interface Props {
  translationFunction: TFunction;
  selectedRows: GridRowSelectionModel;
  setSelectedRows: (rows: GridRowSelectionModel) => void;
  checklists?: Checklist[];
  handleCreateClick: () => Promise<void>;
  disableVirtualization?: boolean;
}

export const SchedulesDataGrid = ({
  translationFunction,
  selectedRows,
  setSelectedRows,
  checklists,
  handleCreateClick,
  disableVirtualization = false,
}: Props) => {
  const columns: DatagridColumn[] = [
    {
      field: 'day',
      renderCell: (params) => (
        <Body
          strong
          size="medium"
          data-testid="schedules-data-grid-group-header"
        >
          {params.value}
        </Body>
      ),
      groupingValueGetter: (params) => {
        return params.value;
      },
    },
    { field: 'externalId', headerName: 'External ID', flex: 2 },
    { field: 'sourceId', headerName: 'Source ID', flex: 2 },
    {
      field: 'startTime',
      headerName: translationFunction(
        'SCHEDULES_TABLE_START_TIME',
        'Start time'
      ),
      renderCell: (params) => <DateCell date={params.value} renderTimeInfo />,
      flex: 1,
    },
    {
      field: 'endTime',
      headerName: translationFunction('SCHEDULES_TABLE_END_TIME', 'End time'),
      renderCell: (params) => <DateCell date={params.value} renderTimeInfo />,
      flex: 1,
    },
    {
      field: 'status',
      headerName: translationFunction('STATUS', 'Status'),
      flex: 1,
    },
  ];

  const groupingColumnDefintion: GridColDef = {
    field: 'day',
    flex: 2,
    headerName: translationFunction('SCHEDULES_TABLE_DAY', 'Day'),
  };

  const dataGridData =
    checklists?.map((checklist) =>
      checklistToSchedulesDataGridRow(checklist)
    ) ?? [];

  const sortedDataGridData = sortByMultipleStringProperties(dataGridData, [
    'startTime',
    'externalId',
  ]);

  const onRowSelectionChange = (nextSelectedRows: GridRowSelectionModel) => {
    setSelectedRows(nextSelectedRows);
  };

  const onToolbarClose = () => {
    setSelectedRows([]);
  };

  return (
    <S.ScrollableDataGridWrapper>
      <DataGrid
        data-testid="schedules-data-grid"
        size="large"
        data={sortedDataGridData}
        columns={columns}
        pagination={false}
        rowGroupingModel={['day']}
        isGroupExpandedByDefault={(group) => group.groupingField === 'day'}
        groupingColDef={groupingColumnDefintion}
        columnVisibilityModel={{
          day: false,
        }}
        checkboxSelection
        onRowSelectionChange={onRowSelectionChange}
        isRowSelectable={(params) => {
          return !String(params.id).includes('auto-generated-row-day/');
        }}
        selectedRows={selectedRows}
        disableVirtualization={disableVirtualization}
      />
      {selectedRows.length > 0 && (
        <SchedulesToolbar
          selectedRows={selectedRows}
          onClose={onToolbarClose}
          onDeleteClick={() => console.log('delete')}
          onCreateClick={handleCreateClick}
        />
      )}
    </S.ScrollableDataGridWrapper>
  );
};
